import { Component, EventEmitter, Input, OnDestroy, Output, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AshcorpPasswordRequirementConfig } from './password-requirement.model';

@Component({
    selector: 'ashcorp-password-requirement',
    templateUrl: './password-requirement.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class AshcorpPasswordRequirementComponent {
    @Input() formGroup: UntypedFormGroup;
    @Input() config!: AshcorpPasswordRequirementConfig;
    @Output() submitCompleted: EventEmitter<any> = new EventEmitter<any>();

    isNewPasswordVisible: boolean = false;
    isConfirmPasswordVisible: boolean = false;
    isSubmitting: boolean = false;

    private _newPasswordSyncSubscription: Subscription | null = null;

    get showOldPassword(): boolean {
        return !!this.config?.showOldPassword;
    }

    get oldPasswordControl(): string {
        return this.config?.oldPasswordControl || 'currentPassword';
    }

    get newPasswordControl(): string {
        return this.config?.newPasswordControl || 'newPassword';
    }

    get confirmPasswordControl(): string {
        return this.config?.confirmPasswordControl || 'confirmPassword';
    }

    get oldPasswordLabel(): string {
        return this.config?.oldPasswordLabel || 'Current password';
    }

    get newPasswordLabel(): string {
        return this.config?.newPasswordLabel || 'New password';
    }

    get confirmPasswordLabel(): string {
        return this.config?.confirmPasswordLabel || 'Confirm password';
    }

    get submitButtonLabel(): string {
        return this.config?.submitButtonLabel || 'Submit';
    }

    get requirementPosition(): 'top' | 'bottom' | 'left' | 'right' {
        return this.config?.requirementPosition || 'right';
    }

    get minLength(): number {
        return this.config?.minLength || 8;
    }

    get mismatchErrorKey(): string {
        return this.config?.mismatchErrorKey || 'passwordMismatch';
    }

    get isSubmitDisabled(): boolean {
        return this.isSubmitting || !!this.config?.submitDisabled || !!this.formGroup?.invalid;
    }

    checkPasswordRequirement(requirement: string): boolean {
        const password = this.formGroup?.get(this.newPasswordControl)?.value || '';

        switch (requirement) {
            case 'length':
                return password.length >= this.minLength;
            case 'uppercase':
                return /[A-Z]/.test(password);
            case 'lowercase':
                return /[a-z]/.test(password);
            case 'number':
                return /[0-9]/.test(password);
            case 'special':
                return /[!@#$%^&*(),.?":{}|<>]/.test(password);
            default:
                return false;
        }
    }

    hasControlError(controlName: string, errorName: string): boolean {
        return !!this.formGroup?.get(controlName)?.hasError(errorName);
    }

    hasPasswordMismatch(): boolean {
        if (this.isNewPasswordVisible) {
            return false;
        }

        return !!this.formGroup?.hasError(this.mismatchErrorKey) && !this.hasControlError(this.confirmPasswordControl, 'required');
    }

    arePasswordsEqual(): boolean {
        const newPassword = this.formGroup?.get(this.newPasswordControl)?.value || '';
        const confirmPassword = this.formGroup?.get(this.confirmPasswordControl)?.value || '';

        if (!newPassword || !confirmPassword) {
            return false;
        }

        return newPassword === confirmPassword;
    }

    onSubmit(): void {
        if (this.isSubmitDisabled || !this.config?.submit) {
            return;
        }

        this.config.beforeSubmit?.();
        this.isSubmitting = true;
        this.formGroup?.disable({ emitEvent: false });

        this.config
            .submit()
            .pipe(
                finalize(() => {
                    this.isSubmitting = false;
                    this.formGroup?.enable({ emitEvent: false });
                    this.config.afterSubmit?.();
                })
            )
            .subscribe({
                next: (response) => {
                    if (!this._isSuccessfulResponse(response)) {
                        this.config.onError?.(response);
                        return;
                    }

                    this.config.onSuccess?.(response);
                    this.submitCompleted.emit(response);
                },
                error: (error) => {
                    this.config.onError?.(error);
                }
            });
    }

    toggleNewPasswordVisibility(): void {
        this.isNewPasswordVisible = !this.isNewPasswordVisible;

        if (this.isNewPasswordVisible) {
            this.isConfirmPasswordVisible = false;
            this._syncConfirmWithNewPassword();
            return;
        }

        this._stopSyncConfirmWithNewPassword();
    }

    toggleConfirmPasswordVisibility(): void {
        this.isConfirmPasswordVisible = !this.isConfirmPasswordVisible;
    }

    ngOnDestroy(): void {
        this._stopSyncConfirmWithNewPassword();
    }

    private _syncConfirmWithNewPassword(): void {
        const newPasswordControl = this.formGroup?.get(this.newPasswordControl);
        const confirmPasswordControl = this.formGroup?.get(this.confirmPasswordControl);

        if (!newPasswordControl || !confirmPasswordControl) {
            return;
        }

        confirmPasswordControl.setValue(newPasswordControl.value || '', { emitEvent: false });
        confirmPasswordControl.markAsDirty();
        confirmPasswordControl.markAsTouched();

        this._stopSyncConfirmWithNewPassword();
        this._newPasswordSyncSubscription = newPasswordControl.valueChanges.subscribe((value) => {
            confirmPasswordControl.setValue(value || '', { emitEvent: false });
            confirmPasswordControl.markAsDirty();
            confirmPasswordControl.markAsTouched();
        });
    }

    private _stopSyncConfirmWithNewPassword(): void {
        this._newPasswordSyncSubscription?.unsubscribe();
        this._newPasswordSyncSubscription = null;
    }

    private _isSuccessfulResponse(response: any): boolean {
        if (!response || typeof response !== 'object') {
            return true;
        }

        if (response.success === false || response.status === false || response.ok === false) {
            return false;
        }

        if (response.error === true) {
            return false;
        }

        return true;
    }
}
